/**
 * usePWAInstall — Capture et expose le prompt d'installation PWA.
 *
 * Le navigateur n'émet `beforeinstallprompt` qu'une seule fois et peut le
 * faire avant que le composant consommateur soit monté. On capture donc au
 * niveau module dès l'import du fichier, puis on diffuse aux hooks actifs
 * via un petit bus d'abonnement (pas de dépendance externe nécessaire).
 */
import { useEffect, useState, useCallback } from 'react';

let deferredPrompt = null;
const listeners = new Set();

const notify = (value) => listeners.forEach((cb) => cb(value));

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify(e);
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify(null);
  });
}

/** Détection plateforme côté client — pilote l'affichage d'instructions dédiées. */
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  const isIOS     = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isEdge    = /Edg\//i.test(ua);
  const isChrome  = /Chrome/i.test(ua) && !isEdge;
  const isSafari  = /Safari/i.test(ua) && !isChrome && !isEdge && !isFirefox;

  if (isIOS)                    return 'ios';           // Safari iOS / Chrome iOS (tous WebKit)
  if (isAndroid && isFirefox)   return 'firefox-android';
  if (isAndroid)                return 'android';       // Chrome/Edge/Samsung
  if (isFirefox)                return 'firefox-desktop';
  if (isSafari)                 return 'safari-desktop';
  if (isChrome || isEdge)       return 'chromium-desktop';
  return 'unknown';
}

/** L'app tourne-t-elle déjà en mode installé (standalone) ? */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

export default function usePWAInstall() {
  const [prompt, setPrompt] = useState(deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const cb = (value) => {
      setPrompt(value);
      if (value === null) setInstalled(true);
    };
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return 'unavailable';
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    deferredPrompt = null;
    notify(null);
    return outcome; // 'accepted' | 'dismissed'
  }, [prompt]);

  return {
    canInstall: !!prompt && !installed,
    isStandalone: installed,
    platform: detectPlatform(),
    install,
  };
}
